<script setup lang="ts">
import { ref, onBeforeMount } from 'vue'
import { DMultiselect, DBtn } from 'front-library'
import {
  getCompanies,
  getPayrolls,
  getProcesses
} from '@/app/core/services/receipt-templates'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    label: string
    modelValue: any
    options: any
    error?: string
    required?: boolean
    disabled: boolean
  }>(),
  {
    label: '',
    modelValue: () => ({}),
    options: () => ({}),
    error: '',
    required: false,
    disabled: false
  }
)

const emit = defineEmits(['update:modelValue'])

const emitUpdate = (val: object) => {
  emit('update:modelValue', val)
}

const { t } = useI18n()
const isShowOptions = ref(false)
const divOptions = ref<any>(null)
const companies = ref<any[]>([])
const process = ref<string[]>([])
const payrolls = ref<string[]>([])
const itemsCompanies = ref<any[]>([])
const itemsProcess = ref<string[]>([])
const itemsPayrolls = ref<string[]>([])

const showOptions = () => {
  isShowOptions.value = true
  companies.value = props.modelValue.companies
  payrolls.value = props.modelValue.payrolls
  process.value = props.modelValue.process
  setTimeout(() => {
    divOptions.value.focus()
  }, 1)
}

const onCloseOptions = () => {
  const options = {
    companies: companies.value,
    payrolls: payrolls.value,
    process: process.value
  }
  emitUpdate(options)
  isShowOptions.value = false
}

const getListPayrolls = async (idCompanies: string[]) => {
  if (!idCompanies) return
  const strCompanies =
    idCompanies?.length === 0 ? '' : "'" + idCompanies?.join("','") + "'"
  itemsPayrolls.value = await getPayrolls(strCompanies)
}

onBeforeMount(async () => {
  itemsCompanies.value = await getCompanies()
  itemsProcess.value = await getProcesses()
  await getListPayrolls(props.modelValue.companies)
})
</script>
<template>
  <div class="relative block">
    <label class="block text-sm font-medium text-gray-800 mb-1.5" v-if="label">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>
    <label
      @click="showOptions"
      style="height: 37.82px"
      class="min-w-max inline-flex gap-5 items-center rounded-md shadow-sm px-2 cursor-pointer border border-red-900 hover:border-blue-700"
      :class="[error === '' ? 'btn-block border-gray-300' : 'border-red-500']">
      <span class="items">
        {{ props.modelValue.companies?.length }}
        {{ t('receiptTemplates.components.companies') }}
      </span>
      <span class="items">
        {{ props.modelValue.payrolls?.length }}
        {{ t('receiptTemplates.components.payrolls') }}
      </span>
      <span class="items">
        {{ props.modelValue.process?.length }}
        {{ t('receiptTemplates.components.processes') }}
      </span>
    </label>
    <div
      v-if="isShowOptions"
      ref="divOptions"
      class="z-50 absolute bg-white border border-gray-100 left-0 bottom-0 mt-2 right-0 rounded-xl shadow-xl outline-none overflow-hidden">
      <input
        class="opacity-0 hidden"
        @input="($event: any) => emitUpdate($event.target.value)"
        :value="modelValue" />
      <div class="h-fit rounded-xl cursor-default border w-full p-5">
        <div class="flex flex-col gap-2">
          <DMultiselect
            :label="t('receiptTemplates.components.companies')"
            v-model="companies"
            :header-title="t('receiptTemplates.components.companies')"
            :disabled="disabled"
            :options="itemsCompanies"
            @change="getListPayrolls(companies)" />
          <DMultiselect
            :label="t('receiptTemplates.components.payrolls')"
            v-model="payrolls"
            :header-title="t('receiptTemplates.components.payrolls')"
            :disabled="disabled"
            :options="itemsPayrolls" />
          <DMultiselect
            :label="t('receiptTemplates.components.processes')"
            :header-title="t('receiptTemplates.components.processes')"
            v-model="process"
            :options="itemsProcess" />
        </div>
        <hr class="mt-3" />
        <div class="mt-3 text-right">
          <DBtn
            @click="onCloseOptions"
            v-html="t('receiptTemplates.components.accept')" />
        </div>
      </div>
    </div>
    <span class="text-xs text-red-500">{{ error }}</span>
  </div>
</template>
<style scoped>
.items {
  border-radius: 3rem;
  padding: 1px 10px;
  font-weight: 600;
  background-color: rgb(255, 237, 213);
  color: rgb(194, 65, 12);
  font-size: 14px;
}
</style>
